import { AddressLine, Alert, Card, PageHeader } from "@/components/ui";
import {
  ASETQU_TOKEN,
  CLAIM_CONTRACT,
  LEGACY_TOKEN,
  NETWORK_NAME,
  PERMANENT_LOCK_VAULT,
  STAKING,
  TOKEN_SYMBOL,
  UTILITY_REGISTRY,
  CHAIN_ID,
  explorerAddress,
} from "@/lib/config";
import { browserPublicClient as publicClient } from "@/lib/chain.client";
import { swapAbi, utilityRegistryAbi } from "@/lib/contracts";
import { formatDisplay, formatTimestamp } from "@/lib/format";
import { useAsync } from "@/lib/use-async";
import { STAKING_ENABLED } from "@/lib/features";
import { usePageMeta } from "@/lib/page-meta";


async function readSwapInfo() {
  if (!CLAIM_CONTRACT) return null;

  try {
    const [start, end, walletCap] = await Promise.all([
      publicClient.readContract({ address: CLAIM_CONTRACT, abi: swapAbi, functionName: "SWAP_START" }),
      publicClient.readContract({ address: CLAIM_CONTRACT, abi: swapAbi, functionName: "SWAP_END" }),
      publicClient.readContract({ address: CLAIM_CONTRACT, abi: swapAbi, functionName: "WALLET_CAP" }),
    ]);
    return { start, end, walletCap };
  } catch {
    return null;
  }
}

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

/**
 * The credential contract, from the registry rather than from configuration.
 *
 * There is deliberately no env var for this. The registry binds it write-once
 * on-chain, so the chain already holds the answer; a second copy in a config
 * file would be a second thing that could be pointed at a forgery — on the one
 * page whose entire job is telling people which addresses are real.
 *
 * Null covers both "no registry configured" and "nothing bound yet", which the
 * page renders the same way it renders any undeployed contract.
 */
async function readCertificateContract() {
  if (!UTILITY_REGISTRY) return null;

  try {
    const address = await publicClient.readContract({
      address: UTILITY_REGISTRY,
      abi: utilityRegistryAbi,
      functionName: "certificateContract",
    });
    return address.toLowerCase() === ZERO_ADDRESS ? null : address;
  } catch {
    // Unreadable is not "not deployed", but on this page both mean the same
    // thing to a visitor: there is no address here to check against.
    return null;
  }
}

export function ContractAddressesPage() {
  usePageMeta({
    title: "Kontrak Resmi AsetQu",
    description:
      "Daftar alamat kontrak resmi AsetQu. Verifikasi sebelum menandatangani.",
  });

  const swapInfo = useAsync(readSwapInfo);
  const certificateContract = useAsync(readCertificateContract);

  const deployed = [
    { label: "Token AsetQu baru", address: ASETQU_TOKEN },
    { label: "Kontrak swap", address: CLAIM_CONTRACT },
    { label: "Vault kunci permanen", address: PERMANENT_LOCK_VAULT },
    ...(STAKING_ENABLED ? [{ label: "Kontrak staking", address: STAKING }] : []),
    // Undefined while the read is in flight, which renders as "Belum dideploy" —
    // briefly wrong, and the reason this row is last rather than beside the
    // token. A row that flickered next to the swap contract would be read as a
    // claim about the swap contract.
    { label: "Sertifikat vendor (NFT)", address: certificateContract ?? null },
  ];

  return (
    <div className="page-shell">
      <PageHeader
        context="Contract authority"
        title="KONTRAK RESMI ASETQU"
        description={`Jaringan ${NETWORK_NAME} (chainId ${CHAIN_ID}). Cocokkan alamat penuh di bawah dengan yang muncul di wallet sebelum memberi izin token atau menjalankan migrasi.`}
        meta={
          <>
            <span>
              Jaringan <strong>{NETWORK_NAME}</strong>
            </span>
            <span>
              Chain ID <strong>{CHAIN_ID}</strong>
            </span>
          </>
        }
      />

      <div data-reveal>
        <Alert tone="warn" title="ALAMAT DI LUAR HALAMAN INI BELUM TERVERIFIKASI">
          Alamat resmi hanya diumumkan melalui domain ini dan kanal resmi yang
          tercantum di halaman Security &amp; FAQ. Alamat yang dikirim lewat DM,
          komentar, atau grup chat harus dianggap penipuan sampai terbukti sebaliknya.
          Ini berlaku juga untuk alamat <code className="font-mono">spender</code>{" "}
          pada permintaan approve.
        </Alert>
      </div>

      <div className="registry-grid">
        <Card title="Kontrak aktif" className="registry-grid__primary contract-ledger" reveal>
          {deployed.every((item) => item.address === null) ? (
            <Alert tone="info">
              Kontrak baru belum dideploy. Alamat akan dipublikasikan di sini
              setelah deployment selesai dan source terverifikasi di explorer.
            </Alert>
          ) : (
            <div className="address-list">
              {deployed.map((item) =>
                item.address ? (
                  <AddressLine
                    key={item.label}
                    label={item.label}
                    address={item.address}
                    href={explorerAddress(item.address)}
                  />
                ) : (
                  <div key={item.label} className="address-row address-row--empty">
                    <span className="address-row__label">{item.label}</span>
                    <span>Belum dideploy</span>
                  </div>
                ),
              )}
            </div>
          )}
          {certificateContract ? (
            <p className="mt-4 text-sm text-ink-muted">
              Alamat sertifikat tidak diambil dari konfigurasi portal. Ia dibaca dari{" "}
              <code className="font-mono">certificateContract()</code> pada UtilityRegistry, yang
              mengikatnya sekali-tulis di rantai — jadi yang tampil di sini adalah apa yang
              registry sendiri akui, bukan apa yang portal disetel untuk mengatakannya.
            </p>
          ) : null}
        </Card>

        <div className="registry-grid__secondary">
          <Card
            title="Kontrak legacy"
            description="Tidak lagi memiliki utilitas resmi kecuali untuk swap ke token baru. Jangan membeli, menjual, atau menukarkannya melalui pihak ketiga."
            className="ui-card--compact"
            reveal
          >
            {LEGACY_TOKEN ? (
              <AddressLine
                label="Token lama"
                address={LEGACY_TOKEN}
                href={explorerAddress(LEGACY_TOKEN)}
              />
            ) : (
              <div className="address-row address-row--empty">
                <span className="address-row__label">Token lama</span>
                <span>Belum dikonfigurasi</span>
              </div>
            )}
            <p className="registry-note">
              Ownership sudah di-renounce: <code className="font-mono">owner()</code>{" "}
              mengembalikan alamat nol. Hanya kontrak swap resmi di atas yang
              boleh menerima <code className="font-mono">approve</code> pada token ini.
            </p>
          </Card>

          <Card title="Parameter swap yang berlaku" className="ui-card--compact" reveal revealDelay={120}>
            {swapInfo ? (
              <dl className="snapshot-list">
                <div>
                  <dt className="text-ink-muted">Rasio</dt>
                  <dd className="font-mono text-xs sm:text-sm">1:1</dd>
                </div>
                <div>
                  <dt className="text-ink-muted">Batas per wallet</dt>
                  <dd className="font-mono text-xs sm:text-sm">
                    {formatDisplay(swapInfo.walletCap)} {TOKEN_SYMBOL}
                  </dd>
                </div>
                <div>
                  <dt className="text-ink-muted">Periode swap</dt>
                  <dd className="font-mono text-xs sm:text-sm">
                    {formatTimestamp(swapInfo.start)} — {formatTimestamp(swapInfo.end)} WIB
                  </dd>
                </div>
              </dl>
            ) : (
              <Alert tone="info">
                Parameter swap belum dapat dibaca dari jaringan saat ini.
              </Alert>
            )}
            <p className="registry-note">
              Alamat yang terdaftar dalam denylist kontrak (lock legacy dan
              treasury perusahaan) tidak dapat memanggil <code className="font-mono">swap</code>{" "}
              sama sekali — lihat halaman Security &amp; FAQ.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
